<template>
    <div class="card" v-show="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.roles') }}</h3>
                </div>
                <div class="col-6 text-right">
                    <base-button type="primary" size="sm" @click="ctrl.create()">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('roles.new') }}</span>
                    </base-button>
                </div>
            </div>
        </div>

        <p class="card-body pt-0 text-sm" v-if="empty">{{ $t('roles.no-role') }}</p>

        <div class="pt-2" v-if="!empty">
            
            <table-control :paging="ctrl.paging" :search="true" v-model="search"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows">

                <el-table-column :label="$t('common.name')">
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            {{ row.name }}
                            <i class="far fa-hand-pointer ml-1" v-if="meta(row).selectable_by_supervisors"></i>
                        </h4>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.permissions')">
                    <template v-slot="{row}">
                        <badge type="primary" class="mr-1"
                            v-for="permission in rolePermissions(row)" :key="permission.key"
                        >
                            {{ permission.name }}
                        </badge>
                    </template>
                </el-table-column>

                <el-table-column align="right">
                    <template v-slot="{row}">
                        <div class="table-actions">

                            <a href="#" class="table-action text-danger" @click.prevent="ctrl.delete(row.id)">
                                <i class="tim-icons icon-simple-remove"></i>
                            </a>

                            <a href="#" class="table-action text-primary" @click.prevent="ctrl.update(row.id)">
                                <i class="tim-icons icon-pencil"></i>
                            </a>
                        </div>
                    </template>
                </el-table-column>
                
            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.edit"
                :title="$t('common.role')"
                :loading="ctrl.form.loading"
            >
                <role-form v-model="ctrl.form.data"
                    :errors="ctrl.form.errors"
                    :modal="ctrl.modals.edit"
                    :permissions="sortedPermissions"
                >
                </role-form>
            </form-modal>

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('roles.delete-confirm') }}</p>
            </confirm-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import RoleForm from './RoleForm'

    export default {

        components: {
            RoleForm
        },

        data() {
            return {
                loaded: false,
                ctrl: new CrudController({
                    endpoint: '/trax/api/front/roles',
                    mapData: this.mapData,
                    unmapData: this.unmapData,
                    paging: {
                        perPage: 10,
                        perPageOptions: [5, 10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['name'],
                        include: ['permissions']
                    }
                }),
                search: '',
                permissions: {},
            }
        },

        computed: {

            empty() {
                return this.ctrl.rows.length == 0 && !this.search
            },

            sortedPermissions() {
                
                // First, merge the permissions of each category.
                let permissions = Object.keys(this.permissions).reduce((cumulated, key) => {
                    let current = this.permissions[key].permissions
                    return {...cumulated, ...current}
                }, {})

                // Then, make an array...
                return Object.keys(permissions).reduce((cumulated, key) => {
                    cumulated.push({ key, name: permissions[key].name })
                    return cumulated
                }, [])

                // and sort it by name.
                .sort((a, b) => {
                    if ( a.name < b.name ) return -1;
                    if ( a.name > b.name ) return 1;
                    return 0;                
                })
            }
        },

        created() {
            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.permissions = resp.data.included.permissions
            })
        },

        watch: {
            search(val) {
                this.ctrl.filterMore({ name: {$text: val} })
            }
        },

        methods: {
            
            rolePermissions(role) {
                return this.sortedPermissions.filter(permission => role.permissions[permission.key])
            },

            meta(role) {
                return {
                    selectable_by_supervisors: !Array.isArray(role.meta) && role.meta.selectable_by_supervisors
                }
            },

            mapData(role) {
                return {
                    id: role ? role.id : null,
                    name: role ? role.name : null,
                    meta: role ? this.meta(role) : { selectable_by_supervisors: false },
                    permissions: role && !Array.isArray(role.permissions) ? role.permissions : {},
                }
            },

            unmapData(data) {
                return data
            }
        }
    }
</script>

<style scoped>

    .card >>> .badge {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
</style>
